/**
 * Like/dislike ratios, shared by every page that lists users.
 *
 * Two different questions, deliberately kept apart:
 *   received — how everyone else rates this profile (the row tint comes from
 *              this one; a profile nobody likes is the one worth reviewing)
 *   given    — how this user swipes on everyone else (shown, never tinted;
 *              an account that likes everything it sees is a different signal)
 */

export type RatioDirection = 'received' | 'given';

/**
 * A handful of swipes says nothing: 1 like and 2 dislikes is "33%". Below this
 * many votes the percentage is withheld and the row stays uncoloured, while
 * the raw counts still show so you can see who is close to qualifying.
 */
export const LIKE_RATIO_MIN_VOTES = 10;

export interface LikeRatioRow {
  like_received_count?: number;
  dislike_received_count?: number;
  like_given_count?: number;
  dislike_given_count?: number;
}

const countsFor = (row: LikeRatioRow, direction: RatioDirection): [number, number] => (
  direction === 'given'
    ? [Number(row?.like_given_count ?? 0), Number(row?.dislike_given_count ?? 0)]
    : [Number(row?.like_received_count ?? 0), Number(row?.dislike_received_count ?? 0)]
);

/** Share of swipes that were likes, or null while the vote count is too small. */
export const likeRatio = (
  row: LikeRatioRow,
  direction: RatioDirection = 'received',
): number | null => {
  const [likes, dislikes] = countsFor(row, direction);
  const total = likes + dislikes;
  if (total < LIKE_RATIO_MIN_VOTES) {
    return null;
  }
  return likes / total;
};

/** "405 / 530 (43.32%)", or just "4 / 3" below the vote floor. */
export const likeRatioLabel = (
  row: LikeRatioRow,
  direction: RatioDirection = 'received',
): string => {
  const [likes, dislikes] = countsFor(row, direction);
  const ratio = likeRatio(row, direction);
  if (ratio === null) {
    return `${likes} / ${dislikes}`;
  }
  return `${likes} / ${dislikes} (${(ratio * 100).toFixed(2)}%)`;
};

/**
 * Row tint from the RECEIVED ratio. Every rated profile gets a colour — the
 * band from 25% to 90% used to get none, and on production that is where most
 * rated users sit (20 of the first 30 reported users, 29 of the first 30 rated
 * users), so the page looked like the tint was broken rather than like most
 * profiles are unremarkable.
 *
 *   < 10%        red          the audience actively rejects this profile
 *   10% - 25%    orange       mostly passed over
 *   25% - 90%    pale green   unremarkable, and fine
 *   >= 90%       green        a standout profile
 *
 * Returns '' only while the profile has too few votes to judge, so it can be
 * dropped straight into an ngClass binding.
 */
export const likeRatioRowClass = (row: LikeRatioRow): string => {
  const ratio = likeRatio(row, 'received');
  if (ratio === null) {
    return '';
  }
  if (ratio >= 0.9) {
    return 'like-ratio-high';
  }
  if (ratio < 0.1) {
    return 'like-ratio-low';
  }
  if (ratio < 0.25) {
    return 'like-ratio-medium-low';
  }
  return 'like-ratio-mid';
};
